// oxlint-disable-next-line no-restricted-imports
import { Combobox as MantineCombobox, useCombobox } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { cloneElement } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { DROPDOWN_Z_INDEX } from "#app/theme/v2/zIndex";
import { Text } from "#components/atoms/Typographies/Text";

import classes from "./Select.module.css";

type SelectDropdownTrigger = React.ReactElement<{
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
}>;

type SelectDropdownProps = {
  trigger: SelectDropdownTrigger;
  label?: string;
  data: { value: string; label: string }[] | string[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  nothingFoundMessage?: string;
  width?: number | "target";
  closeOnSelect?: boolean;
};

const normalizeData = (
  data: { value: string; label: string }[] | string[],
): { value: string; label: string }[] =>
  data.map((item) => (typeof item === "string" ? { value: item, label: item } : item));

const SelectDropdown = ({
  trigger,
  label,
  data,
  value,
  onChange,
  disabled,
  nothingFoundMessage,
  width = "target",
  closeOnSelect = true,
}: SelectDropdownProps) => {
  const { t } = useTranslation("components");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const normalizedData = normalizeData(data);

  const handleOptionSubmit = (val: string) => {
    onChange?.(val === value ? null : val);
    if (closeOnSelect) {
      combobox.closeDropdown();
    }
  };

  return (
    <MantineCombobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      shadow="none"
      width={width}
      position="bottom-start"
      zIndex={DROPDOWN_Z_INDEX}
      classNames={{
        dropdown: classes.dropdown,
        option: classes.option,
      }}
    >
      <MantineCombobox.Target>
        {cloneElement(trigger, {
          disabled: disabled ?? trigger.props.disabled,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            trigger.props.onClick?.(event);
            if (!disabled) {
              combobox.toggleDropdown();
            }
          },
        })}
      </MantineCombobox.Target>

      <MantineCombobox.Dropdown>
        {label && (
          <Text
            size="tiny"
            type="medium"
            color={colors.text.secondaryDefault}
            style={{ padding: `${spacing[2]} ${spacing[2]} ${spacing[2]}`, width: "100%" }}
          >
            {label}
          </Text>
        )}
        <MantineCombobox.Options>
          {normalizedData.length === 0 ? (
            <MantineCombobox.Empty>
              {nothingFoundMessage ?? t("select.nothingFound")}
            </MantineCombobox.Empty>
          ) : (
            normalizedData.map((item) => (
              <MantineCombobox.Option
                key={item.value}
                value={item.value}
                active={item.value === value}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <span>{item.label}</span>
                  {item.value === value && (
                    <IconCheck size={16} color={colors.icon.primaryDefault} />
                  )}
                </div>
              </MantineCombobox.Option>
            ))
          )}
        </MantineCombobox.Options>
      </MantineCombobox.Dropdown>
    </MantineCombobox>
  );
};

export { SelectDropdown };
